import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { CareerIcon } from 'atoms/CareerIcon/CareerIcon';
import { ICareerSkillNode } from 'core/hooks/CareerSkillGraph';
import { ISkillProfession } from 'core/hooks/Growth/transform/transformSkillDetail';
import { PATHS } from 'router/config';

import { formatCareerDate } from 'services/DateService';
import {
  BlockTitle,
  Card,
  Details,
  Fact,
  FactRow,
  GhostCta,
  Meta,
  SecondaryLink,
} from './passportStyles';
import { SkillProfessionRow } from './SkillProfessionRow';

/** Сколько профессий показываем сразу; остальные — под «ещё N». */
const VISIBLE = 5;

/**
 * Правая колонка: «где этот навык нужен» (ТЗ 0070, разделы 4.7.4, 4.7.7,
 * 4.7.10).
 *
 * До неё паспорт отвечал на этот вопрос ровно одним числом вакансий. Навык на
 * карте выглядел самоценной галочкой, хотя карта строится вокруг ОДНОЙ
 * выбранной профессии, а тот же Docker нужен ещё в четырёх.
 *
 * 🔴 Число вакансий берётся из УЗЛА карты, а не из детального запроса: только
 * у узла есть `computedAt`, а показывать счётчик без даты нельзя — он
 * выглядит живым и тогда, когда ночная задача не отрабатывала неделю. Взяв
 * число из другого источника, мы получили бы на одном экране два счётчика из
 * снимков разного возраста.
 *
 * 🔴 Гейт ленты вакансий приходит СВЕРХУ: право называет сервер
 * (`myCareerJourney.locked`). Само число не-члену показываем — это факт о
 * нашей доске, и он же мотивирует вступить; закрыт только переход.
 */
interface ISkillPassportWhereNeeded {
  node: ICareerSkillNode;
  professions: ISkillProfession[];
  loading: boolean;
  canOpenVacancies: boolean;
  vacanciesLocked: boolean;
}

export const SkillPassportWhereNeeded = memo(
  ({
    node,
    professions,
    loading,
    canOpenVacancies,
    vacanciesLocked,
  }: ISkillPassportWhereNeeded) => {
    const { t } = useTranslation();

    const vacancies = node.demand ? node.demand.liveVacancies : 0;
    const shown = professions.slice(0, VISIBLE);
    const rest = professions.slice(VISIBLE);

    return (
      <Section aria-labelledby="skm-where-needed">
        {/* ТЗ 0071: блок живёт внутри «подробнее», поэтому заголовок —
            блочный, а не колонки: колонок у паспорта больше нет. */}
        <BlockTitle id="skm-where-needed">{t('careerCenter.map.whereNeeded')}</BlockTitle>

        {/* Высота держится, пока грузятся профессии: без этого модалка
            подпрыгивает в момент ответа сервера. */}
        <Professions>
          <Meta>{t('careerCenter.map.professionsWhere')}</Meta>
          {loading && professions.length === 0 && (
            <Meta>{t('careerCenter.map.professionsLoading')}</Meta>
          )}
          {!loading && professions.length === 0 && (
            <Meta>{t('careerCenter.map.professionsEmpty')}</Meta>
          )}
          {shown.length > 0 && (
            <List>
              {shown.map((profession) => (
                <SkillProfessionRow key={profession.code} profession={profession} />
              ))}
            </List>
          )}
          {rest.length > 0 && (
            <Details>
              <summary>{t('careerCenter.map.professionsMore', { n: rest.length })}</summary>
              <List>
                {rest.map((profession) => (
                  <SkillProfessionRow key={profession.code} profession={profession} />
                ))}
              </List>
            </Details>
          )}
        </Professions>

        <Card>
          <Meta>{t('careerCenter.map.demand')}</Meta>
          {vacancies > 0 ? (
            <>
              <FactRow>
                <Fact $accent>
                  <CareerIcon name="applications" size={14} />
                  {t('careerCenter.map.demandValue', {
                    n: vacancies,
                    date: formatCareerDate(node.demand?.computedAt) || '—',
                  })}
                </Fact>
              </FactRow>
              {canOpenVacancies && (
                <GhostCta as={Link} to={`${PATHS.careerJobs}?skill=${node.slug}`}>
                  {t('careerCenter.map.openVacancies')}
                </GhostCta>
              )}
              {vacanciesLocked && (
                <Meta>
                  {t('careerCenter.map.vacanciesLocked')}{' '}
                  <SecondaryLink as={Link} to={PATHS.careerCenter}>
                    {t('careerCenter.map.vacanciesLockedCta')}
                  </SecondaryLink>
                </Meta>
              )}
            </>
          ) : (
            // Кнопка в заведомо пустую ленту — обещание, которое экран не
            // выполнит.
            <Meta>{t('careerCenter.map.vacanciesNone')}</Meta>
          )}
        </Card>
      </Section>
    );
  },
);

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const Professions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-height: 152px;
`;

const List = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
  padding: 0;
`;
